/// <reference types="@types/google.maps" />
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export type ReverseGeocodeResult = {
  formattedAddress?: string;
  cityName?: string;
  areaName?: string;
  street?: string;
  postCode?: string;
};

export function useGoogleMaps() {
  const runtimeConfig = useRuntimeConfig();
  const nuxtApp = useNuxtApp();

  const map = ref<google.maps.Map | null>(null);
  const marker = ref<google.maps.Marker | null>(null);
  const mapContainer = ref<HTMLElement | null>(null);
  const isFullscreen = ref(false);

  // Cairo location
  const initialPosition = { lat: 30.064742, lng: 31.249509 };

  const loadGoogleMaps = async () => {
    if (nuxtApp.$appConfig?.integrations.includes("google_maps_integration")) {
      setOptions({
        key: runtimeConfig.public.GOOGLE_API_KEY,
        v: "weekly",
      });

      await importLibrary("maps");
      await importLibrary("marker");
      await importLibrary("places");
    }
  };

  const createMap = (
    mapElement: HTMLElement | null,
    position = initialPosition,
  ) => {
    mapContainer.value = mapElement;

    if (mapElement) {
      // Create a map centered at some default location with fullscreen control disabled
      map.value = new google.maps.Map(mapElement, {
        center: position,
        zoom: 12,
        fullscreenControl: false,
        zoomControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        cameraControl: false,
        mapId: runtimeConfig.public.GOOGLE_MAPS_ID,
      });

      map.value.addListener("click", (event: google.maps.MapMouseEvent) => {
        addMarker(event.latLng!);
      });
    }
  };

  // Function to toggle fullscreen mode for the map
  const toggleFullscreen = () => {
    if (!mapContainer.value) return;

    if (!isFullscreen.value) {
      // Enter fullscreen
      if (mapContainer.value.requestFullscreen) {
        mapContainer.value.requestFullscreen();
      } else if ((mapContainer.value as any).webkitRequestFullscreen) {
        (mapContainer.value as any).webkitRequestFullscreen(); // Safari
      } else if ((mapContainer.value as any).msRequestFullscreen) {
        (mapContainer.value as any).msRequestFullscreen(); // IE11
      }

      isFullscreen.value = true;

      // Trigger resize event after a brief delay to ensure map renders correctly
      setTimeout(() => {
        if (map.value) {
          google.maps.event.trigger(map.value, "resize");
        }
      }, 100);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }

      isFullscreen.value = false;

      // Trigger resize event after exiting fullscreen
      setTimeout(() => {
        if (map.value) {
          google.maps.event.trigger(map.value, "resize");
        }
      }, 100);
    }
  };

  const fullscreenChangeHandler = () => {
    isFullscreen.value = !!document.fullscreenElement;
  };

  // Function to add a marker, remove the previous marker if it exists
  const addMarker = (
    position: google.maps.LatLngLiteral | google.maps.LatLng,
    draggable = true,
  ) => {
    if (!map.value) return;

    // Remove the previous marker if it exists
    if (marker.value) marker.value.setVisible(false);

    marker.value = new google.maps.Marker({
      position,
      map: map.value,
      draggable,
    });

    map.value.setCenter(position);
  };

  // Place or move a draggable marker with a dragend callback
  const placeMarkerWithDrag = (
    position: google.maps.LatLngLiteral,
    onDragEnd: (pos: google.maps.LatLngLiteral) => void,
  ) => {
    if (!map.value) return;

    if (marker.value) {
      marker.value.setPosition(position);
    } else {
      addMarker(position, true);
      if (marker.value) {
        (marker.value as google.maps.Marker).addListener("dragend", () => {
          const pos = marker.value?.getPosition();
          if (pos) {
            onDragEnd({ lat: pos.lat(), lng: pos.lng() });
          }
        });
      }
    }

    map.value.panTo(position);
  };

  const getCurrentLocation = (
    mapElement: HTMLElement | null,
    event?: Event,
  ) => {
    event?.preventDefault();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          addMarker({ lat: latitude, lng: longitude }); // Add a marker at the current location
          if (mapElement && map.value) {
            // Center the map at the current location
            map.value.setCenter({ lat: latitude, lng: longitude });
          }
        },
        () => {
          console.error("Geolocation failed.");
        },
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  // Reverse geocode a lat/lng into address components
  const reverseGeocode = (
    lat: number,
    lng: number,
  ): Promise<ReverseGeocodeResult> => {
    return new Promise((resolve) => {
      if (typeof window === "undefined" || !window.google?.maps?.Geocoder) {
        resolve({});
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== "OK" || !results?.[0]) {
          resolve({});
          return;
        }

        const formattedAddress = results[0].formatted_address;
        const components = results[0].address_components;
        let cityName: string | undefined;
        let areaName: string | undefined;
        let street: string | undefined;
        let postCode: string | undefined;

        for (const component of components) {
          const types = component.types;
          if (types.includes("locality")) {
            cityName = component.long_name;
          } else if (
            types.includes("administrative_area_level_2") &&
            !cityName
          ) {
            cityName = component.long_name;
          } else if (
            types.includes("sublocality_level_1") ||
            types.includes("neighborhood")
          ) {
            areaName = component.long_name;
          } else if (types.includes("route")) {
            street = component.long_name;
          } else if (types.includes("postal_code")) {
            postCode = component.long_name;
          }
        }

        resolve({ cityName, areaName, street, postCode, formattedAddress });
      });
    });
  };

  // Initialize Places Autocomplete on an input element
  const initAutocomplete = (
    inputElement: HTMLInputElement,
    onPlaceChanged: (location: google.maps.LatLngLiteral) => void,
  ) => {
    if (!google.maps?.places) return;

    const autocomplete = new google.maps.places.Autocomplete(inputElement, {
      types: ["geocode"],
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        onPlaceChanged({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
      }
    });
  };

  const pinnedLocation = computed(() =>
    nuxtApp.$appConfig?.integrations.includes("google_maps_integration")
      ? {
          lat: marker.value?.getPosition()?.lat() || initialPosition.lat,
          lng: marker.value?.getPosition()?.lng() || initialPosition.lng,
        }
      : { lat: 0, lng: 0 },
  );

  return {
    map,
    pinnedLocation,
    isFullscreen,
    loadGoogleMaps,
    createMap,
    addMarker,
    placeMarkerWithDrag,
    getCurrentLocation,
    toggleFullscreen,
    fullscreenChangeHandler,
    reverseGeocode,
    initAutocomplete,
  };
}
